<script>
import Chart from 'chart.js';

export default {
  name: 'AdminRevenueChart',
  props: {
    colors: Array,
    data: Array,
    loading: Boolean
  },
  data() {
    return {
      chart: null
    };
  },
  computed: {
    chartData() {
      if (this.data) {
        const days = this.data.map(row => row.day);
        const revenue = days.map(day => {
          const data = this.data.filter(row => row.day === day)[0];
          return data ? data.total / 100 : 0;
        });

        const data = {
          labels: days.map(day => this.$d(this.convertDate(day), 'short')),
          datasets: [
            {
              label: 'Payments',
              data: revenue,
              backgroundColor: this.colors[0],
              borderColor: 'white',
              borderWidth: 2
            }
          ]
        };

        return data;
      }
    },

    totalRevenue() {
      if (this.data && this.data.length) {
        return this.data.map(row => row.total / 100).reduce((accum, val) => accum + val);
      } else {
        return 0;
      }
    }
  },
  watch: {
    data() {
      this.updateChart();
    }
  },
  mounted() {
    this.chart = new Chart(this.$refs.chart, {
      type: 'line',
      options: {
        animation: false,
        title: {
          display: false
        },
        maintainAspectRatio: false,
        responsive: true,
        elements: {
          point: {
            radius: 4,
            hoverRadius: 5
          }
        },
        legend: false,
        scales: {
          xAxes: [
            {
              gridLines: {
                display: false
              }
            }
          ],
          yAxes: [
            {
              stacked: true,
              gridLines: {
                display: false
              },
              ticks: {
                beginAtZero: true,
                callback: value => {
                  if (value % 1 === 0) {
                    // only integers
                    return value.toLocaleString('en-US', {
                      style: 'currency',
                      currency: 'USD',
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0
                    });
                  }
                }
              }
            }
          ]
        },
        tooltips: {
          mode: 'index',
          intersect: false,
          callbacks: {
            label: (tooltipItem, data) => {
              const label = data.datasets[tooltipItem.datasetIndex].label;
              const value = parseInt(data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index]).toLocaleString(
                'en-US',
                {
                  style: 'currency',
                  currency: 'USD',
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0
                }
              );
              return `${label} (${value})`;
            }
          }
        }
      }
    });
    this.updateChart();
  },
  methods: {
    // Converts a date like 2018-08-22 to a date object
    convertDate(string) {
      const year = string.slice(0, 4);
      const month = parseInt(string.slice(5, 7)) - 1;
      const day = string.slice(8, 10);
      return new Date(year, month, day);
    },

    // Updates and renders the chart
    updateChart() {
      if (this.chartData) {
        this.chart.data = this.chartData;
        this.chart.update();
      }
    }
  }
};
</script>

<template>
  <div class="revenue-chart-container">
    <h2 class="revenue-title">
      {{ $n(totalRevenue, 'currencyInteger') }}
      <small class="revenue-subtitle">Revenue</small>
    </h2>

    <div ref="chartContainer" v-loading="loading" class="revenue-chart">
      <canvas ref="chart" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.revenue-title {
  font-size: 2rem;
  text-align: center;
}

.revenue-subtitle {
  display: block;
  font-size: $--font-size-small;
  color: $--color-gray-40;
}

.revenue-chart {
  position: relative;
  height: 12rem;
  margin-bottom: 2rem;

  @include breakpoint('sm-and-down') {
    height: 8rem;
  }
}
</style>
